import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { debounceTime, switchMap, catchError } from 'rxjs/operators';

import { DxcSelectOption } from '../dxc.types';
import { DxcPanelAnchor } from '../panel/dxc-panel-anchor';

let nextId = 0;

/**
 * Autocompletado. A diferencia del select, el usuario escribe texto libre y el
 * panel sugiere: se usa cuando el catálogo es demasiado grande para listarlo o
 * vive en el backend — domicilio, RFC de cliente, búsqueda global.
 *
 * El componente no conoce el backend: recibe `search` y se limita a llamarla
 * con el término, con espera para no consultar en cada tecla.
 */
@Component({
    standalone: true,
    selector: 'dxc-autocomplete',
    templateUrl: './dxc-autocomplete.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcAutocompleteComponent implements OnDestroy {

    /** Consulta remota o local. El componente solo pasa el término. */
    @Input() search: (query: string) => Observable<DxcSelectOption[]> = () => of([]);
    @Input() placeholder = 'Escribe para buscar…';
    @Input() minChars = 2;
    @Input() debounce = 300;
    @Input() invalid = false;
    @Input() disabled = false;
    @Input() ariaLabel = '';
    /**
     * Si se define, la lista ofrece registrar lo tecleado y lo emite por `(create)`.
     * Aparece al final también con coincidencias, salvo que una sea exactamente lo
     * tecleado: ofrecer registrar lo que ya existe fabrica un duplicado.
     */
    @Input() createLabel = '';
    /**
     * Rótulo del atajo de teclado que enfoca este campo — `⌘K`, `Ctrl K`, `/`.
     * Solo dibuja el cartel: quién escucha la tecla es quien tiene el atajo.
     * Un atajo sin cartel no lo descubre nadie.
     */
    @Input() shortcutHint = '';
    /** `id` del campo, para que un `<label for>` lo nombre. */
    @Input() inputId = '';
    /**
     * Qué se queda escrito en el campo al elegir. Por defecto la etiqueta de la
     * opción; un código postal elige «CP 06600 · Juárez» y deja «06600».
     */
    @Input() display: ((option: DxcSelectOption) => string) | null = null;

    /**
     * Lo escrito en el campo. Como entrada sirve para enseñar un valor que ya
     * existe —el código postal guardado— o devolverlo al elegido cuando lo
     * tecleado no llegó a ser una opción.
     */
    @Input() set text(value: string | null | undefined) { this.#text = value ?? ''; this.cdr.markForCheck(); }
    get text() { return this.#text; }
    #text = '';

    @Output() valueChange = new EventEmitter<DxcSelectOption>();
    @Output() create = new EventEmitter<string>();

    @ViewChild('field') private fieldRef?: ElementRef<HTMLInputElement>;

    @HostBinding('class.dxc-combo') readonly hostClass = true;
    @HostBinding('class.is-open') get isOpen() { return this.open; }

    /** Accesor: el ancla del panel `fixed` se engancha y suelta con la apertura. */
    #open = false;
    get open() { return this.#open; }
    set open(value: boolean) {
        if (this.#open === value) return;
        this.#open = value;
        value ? this.anchor.attach() : this.anchor.detach();
    }

    private anchor!: DxcPanelAnchor;
    get panelStyle() { return this.anchor.style; }
    get panelUp() { return this.anchor.up; }
    loading = false;
    results: DxcSelectOption[] = [];
    activeIndex = 0;
    readonly panelId = `dxc-autocomplete-${nextId++}`;

    get term() { return this.text.trim(); }

    /**
     * No existe mientras se consulta —las coincidencias todavía no se saben— ni
     * por debajo de `minChars`, donde el panel no se pinta.
     */
    get showCreate() {
        const term = this.term;
        if (!this.createLabel || this.loading || !term || term.length < this.minChars) return false;
        const wanted = term.toLocaleLowerCase();
        return !this.results.some(option => option.label.trim().toLocaleLowerCase() === wanted);
    }

    get createIndex() { return this.results.length; }

    /** Ids y `aria-activedescendant` solo con `createLabel`, para no alterar el marcado sin ella. */
    optionId(index: number) { return this.createLabel ? `${this.panelId}-opt-${index}` : null; }

    get activeDescendant() {
        if (!this.createLabel || !this.open || this.loading || this.term.length < this.minChars) return null;
        const last = this.results.length - (this.showCreate ? 0 : 1);
        return this.activeIndex >= 0 && this.activeIndex <= last ? this.optionId(this.activeIndex) : null;
    }

    readonly #queries = new Subject<string>();
    readonly #sub: Subscription;

    constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {
        this.anchor = new DxcPanelAnchor(
            this.host.nativeElement, '.dxc-field__wrap', () => this.cdr.markForCheck());
        this.#sub = this.#queries.pipe(
            debounceTime(this.debounce),
            switchMap(query => this.search(query).pipe(catchError(() => of([] as DxcSelectOption[])))),
        ).subscribe(results => {
            this.results = results;
            this.loading = false;
            this.activeIndex = 0;
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy() { this.#sub.unsubscribe(); this.anchor.detach(); }

    onInput(text: string) {
        this.text = text;
        this.open = true;
        if (text.trim().length < this.minChars) {
            this.results = [];
            this.loading = false;
        } else {
            this.loading = true;
            this.#queries.next(text.trim());
        }
        this.cdr.markForCheck();
    }

    choose(option: DxcSelectOption) {
        this.text = this.display ? this.display(option) : option.label;
        this.open = false;
        this.valueChange.emit(option);
        this.cdr.markForCheck();
    }

    /**
     * Vacía el campo desde fuera. Hace falta cuando el filtro que este control
     * puso se retira en otro sitio —la ficha de filtros activos, un «limpiar
     * todo»—: sin esto el campo seguiría mostrando un valor que ya no se aplica.
     */
    reset() {
        this.text = '';
        this.results = [];
        this.open = false;
        this.cdr.markForCheck();
    }

    /**
     * Da el foco desde fuera. El par de `reset()`: hace falta cuando quien
     * decide que hay que buscar no es este campo —un atajo de teclado, un botón
     * de «buscar» de otra región—. Selecciona lo que hubiera escrito para que la
     * segunda búsqueda no empiece por borrar la primera.
     */
    focus() {
        this.fieldRef?.nativeElement.focus();
        this.fieldRef?.nativeElement.select();
    }

    onCreate() {
        this.create.emit(this.term);
        this.open = false;
        this.cdr.markForCheck();
    }

    onKeydown(event: KeyboardEvent) {
        switch (event.key) {
            case 'Escape': this.open = false; break;
            case 'ArrowDown':
            case 'ArrowUp': {
                const last = this.results.length - (this.showCreate ? 0 : 1);
                if (last < 0) return;
                this.activeIndex = Math.min(last, Math.max(0, this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1)));
                if (this.createLabel) this.#scrollActiveIntoView();
                break;
            }
            case 'Enter':
                if (this.open && this.showCreate && this.activeIndex === this.createIndex) { this.onCreate(); break; }
                if (!this.open || !this.results[this.activeIndex]) return;
                this.choose(this.results[this.activeIndex]);
                break;
            default: return;
        }
        event.preventDefault();
        this.cdr.markForCheck();
    }

    /** La fila de crear vive al final: en una lista larga queda bajo el pliegue del panel. */
    #scrollActiveIntoView() {
        const id = this.optionId(this.activeIndex);
        if (id) this.host.nativeElement.querySelector(`#${id}`)?.scrollIntoView({ block: 'nearest' });
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentDown(event: Event) {
        if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
            this.open = false;
            this.cdr.markForCheck();
        }
    }
}
